/**
 * WasteToolbarContent - Herramientas del módulo Waste
 *
 * @version 3.0
 * @updated 2026-02-23
 * @module core/modules/waste/components
 *
 * @description
 * 13 botones de inventario organizados en dos filas (8 + 5).
 * Los elementos sin geometría de mapa (fleet, organizations, crews, persons)
 * disparan un CustomEvent en lugar de activar una herramienta de mapa.
 */

import {
  Warehouse, ArrowDownToLine, Truck, Hexagon, CircleDot,
  Wrench, ArrowLeftRight, Fuel, AlertTriangle, ShieldBan,
  Building2, UsersRound, UserRoundCog, Route,
} from 'lucide-react';
import { useOperationalModeStore } from '../../../operational-modes';
import type { ModuleToolbarProps } from '../../../module-registry';
import './waste-toolbar-content.css';

interface WasteToolDef {
  id: string;
  icon: React.ReactNode;
  tooltip: string;
  isEntity?: boolean; // true = no map geometry, opens create panel via event
}

const ROW1: WasteToolDef[] = [
  {
    id: 'add-depot',
    icon: <Warehouse size={20} />,
    tooltip: 'Depósitos\nBase principal donde se guardan vehículos y comienza o finaliza la operación diaria. Punto de origen y retorno de los servicios.',
  },
  {
    id: 'add-discharge-site',
    icon: <ArrowDownToLine size={20} />,
    tooltip: 'Sitios de descarga\nSitios finales de disposición de materiales o descargas operativas. Cierran el ciclo de cada recorrido.',
  },
  {
    id: 'add-fleet',
    icon: <Truck size={20} />,
    tooltip: 'Flota\nVehículos disponibles para la operación. Determinan capacidad, alcance y cantidad de servicios posibles.',
    isEntity: true,
  },
  {
    id: 'add-zone',
    icon: <Hexagon size={20} />,
    tooltip: 'Zonas\nSectores territoriales que agrupan demanda o tareas. Unidad básica para planificar cobertura y asignar recursos.',
  },
  {
    id: 'add-service-point',
    icon: <CircleDot size={20} />,
    tooltip: 'Puntos de servicio\nUbicaciones específicas donde se presta el servicio o se realiza una intervención. Elementos operativos dentro de cada zona.',
  },
  {
    id: 'add-secondary-depot',
    icon: <Wrench size={20} />,
    tooltip: 'Depósitos secundarios\nInstalaciones de apoyo operativo como talleres, cocheras secundarias o puntos técnicos de mantenimiento.',
  },
  {
    id: 'add-transfer-site',
    icon: <ArrowLeftRight size={20} />,
    tooltip: 'Sitios de transferencia\nPuntos intermedios donde se redistribuyen materiales o cargas antes de su destino final.',
  },
  {
    id: 'add-supply-point',
    icon: <Fuel size={20} />,
    tooltip: 'Puntos de abastecimiento\nLugares de abastecimiento de insumos o energía necesarios para la operación (combustible, carga eléctrica, agua, etc.).',
  },
];

const ROW2: WasteToolDef[] = [
  {
    id: 'add-incident',
    icon: <AlertTriangle size={20} />,
    tooltip: 'Incidencias\nPuntos con eventos, reclamos o situaciones críticas que requieren atención o seguimiento.',
  },
  {
    id: 'add-restriction',
    icon: <ShieldBan size={20} />,
    tooltip: 'Restricciones\nÁreas o tramos con limitaciones operativas que condicionan circulación, acceso o intervención.',
  },
  {
    id: 'add-organization',
    icon: <Building2 size={20} />,
    tooltip: 'Organizaciones\nEntidades responsables de la prestación del servicio o la gestión de activos dentro del sistema.',
    isEntity: true,
  },
  {
    id: 'add-crew',
    icon: <UsersRound size={20} />,
    tooltip: 'Equipos\nEquipos de trabajo asignables a tareas, recorridos o intervenciones específicas.',
    isEntity: true,
  },
  {
    id: 'add-person',
    icon: <UserRoundCog size={20} />,
    tooltip: 'Personas\nPersonas individuales con roles operativos o administrativos dentro del sistema.',
    isEntity: true,
  },
  {
    id: 'draw-route',
    icon: <Route size={20} />,
    tooltip: 'Rutas\nTraza recorridos de recolección conectando puntos en el mapa. Crea trayectorias para planificar rutas de recolección.',
  },
];

export function WasteToolbarContent({
  activeTool,
  onToolChange,
}: ModuleToolbarProps): JSX.Element {
  const { mode } = useOperationalModeStore();
  const isInventoryMode = mode === 'inventory';

  const handleToolClick = (tool: WasteToolDef): void => {
    if (tool.isEntity) {
      window.dispatchEvent(new CustomEvent('waste-open-create-panel', { detail: { type: tool.id } }));
      return;
    }

    // Si es el botón de rutas, abrir el tab de rutas en el sidebar izquierdo
    if (tool.id === 'draw-route') {
      window.dispatchEvent(new CustomEvent('waste-open-routes-tab'));
      console.log('[WasteToolbarContent] Evento despachado: waste-open-routes-tab');
    }

    const newTool = activeTool === tool.id ? null : tool.id;
    void onToolChange(newTool);
  };

  const renderButton = (tool: WasteToolDef): JSX.Element => (
    <button
      key={tool.id}
      className={`toolbar-square-btn ${activeTool === tool.id ? 'active' : ''}`}
      aria-label={tool.tooltip}
      title={!isInventoryMode ? 'Debes estar en modo Inventariar' : tool.tooltip}
      onClick={() => handleToolClick(tool)}
      disabled={!isInventoryMode}
    >
      {tool.icon}
    </button>
  );

  const placeholderCount = 8 - ROW2.length;

  return (
    <>
      {/* Primera fila: 8 elementos */}
      <div className="toolbar-row">
        {ROW1.map(renderButton)}
      </div>

      {/* Segunda fila: 5 elementos + 3 placeholders */}
      <div className="toolbar-row">
        {ROW2.map(renderButton)}
        {Array.from({ length: placeholderCount }, (_, i) => (
          <button
            key={`waste-placeholder-${i}`}
            className="toolbar-square-btn"
            aria-label={`Herramienta waste ${ROW2.length + i + 1}`}
            disabled
          />
        ))}
      </div>
    </>
  );
}
